<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import Slider from 'primevue/slider';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();
const props = defineProps({
  min: {
    type: Number,
    required: true
  },
  max: {
    type: Number,
    required: true
  },
  incrementSize: {
    type: Number,
    required: true
  },
  startLabel: {
    type: String,
    required: true
  },
  endLabel: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean
  },
  valueBadge: Boolean
});
const sliderHandlePosition = ref();

const value = defineModel({
  type: Number
});
const sliderHandlePositionComputed = computed(() => value.value ? sliderHandlePosition.value : undefined);

onMounted(() => {
  updateSliderHandlePosition();
});

const updateSliderHandlePosition = () => {
  // @ts-expect-error this is to get the underlying element beneth primevue slider
  const sliderWidth = sliderRef.value?.$el.offsetWidth;

  // @ts-expect-error this is to get the underlying element beneth primevue slider
  const sliderHandle = sliderRef.value?.$el.querySelector('.p-slider-handle');
  const sliderHandleOffsetLeft = sliderHandle?.offsetLeft;
  const sliderHandleWidth = sliderHandle?.offsetWidth;
  const sliderHandleCenterX = sliderHandleOffsetLeft + sliderHandleWidth / 2;
  sliderHandlePosition.value = `${(sliderHandleCenterX / sliderWidth) * 100}%`;
};

const calculatedVerticalLines = computed(() => {
  return Math.floor((props.max - props.min) / props.incrementSize) + 1;
});

const sliderRef = ref<HTMLElement>();
const sliderBadgeTop = computed(() => (value.value ? '-50px' : '-60px'));

const onChange = () => {
  updateSliderHandlePosition();
};
</script>

<template>
  <div class="custom-slider">
    <Slider v-model="value"
      ref="sliderRef"
      :min="props.min"
      :max="props.max"
      :step="props.incrementSize"
      :disabled="props.disabled"
      @change="onChange"/>

    <div v-if="calculatedVerticalLines" class="vertical-lines">
      <div v-for="i in calculatedVerticalLines"
        :key="i"
        class="vertical-line"></div>
    </div>

    <div v-if="props.valueBadge" class="value-badge">
      {{ value ? value : $t('please_select') }}
    </div>

    <div class="slider-label start-label">{{ props.startLabel }}</div>
    <div class="slider-label end-label">{{ props.endLabel }}</div>
  </div>
</template>

<style scoped lang="scss">
.custom-slider {
  position: relative;
  margin: 48px 24px;
}
:deep(.p-slider-handle) {
  z-index: 5;
}

.vertical-lines {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
}

.vertical-line {
  width: 1.5px;
  height: 15px;
  margin-top: -6px;
  background-color: #94a3b8;
}

.value-badge {
  position: absolute;
  top: v-bind(sliderBadgeTop);
  left: v-bind(sliderHandlePositionComputed);
  transform: translateX(-50%);
  font-size: 0.8rem;
  color: #fff;
  background-color: #333;
  padding: 2px 5px;
  border-radius: 5px;
  text-wrap: wrap;
  max-width: 50px;
  text-align: center;
}

.slider-label {
  position: absolute;
  font-size: 0.8rem;
  color: #666;
  bottom: -36px;
}

.slider-label.start-label {
  left: -24px;
}

.slider-label.end-label {
  right: -24px;
}
</style>
